/**
 * Free Workflow Ops Queue — dedicated pipeline for `intent=free_workflow` leads.
 *
 * Different from AdminPilotCohortPage — those are founding-pilot applicants.
 * These are visitors who clicked "Get One Workflow Automated Free" and are
 * owed a shipped workflow within 5 business days. The pipeline surfaces
 * SLA risk (overdue count) so ops can jump on the stragglers.
 *
 * Feeds off:
 *   GET   /api/admin/free-workflow           — inbox + per-status stats + overdue count
 *   PATCH /api/admin/free-workflow/{id}      — inline status/notes/eta edit
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  Sparkle, ClipboardText, EnvelopeSimple, Phone, WarningOctagon, Clock,
} from "@phosphor-icons/react";
import client from "../lib/api";

const STATUS_META = {
  fw_new:      { label: "New",      color: "border-[#FF5A1F]/40 bg-[#FF5A1F]/[0.06] text-[#FF9764]" },
  fw_scoping:  { label: "Scoping",  color: "border-sky-400/40 bg-sky-400/[0.06] text-sky-300" },
  fw_building: { label: "Building", color: "border-amber-400/40 bg-amber-400/[0.06] text-amber-300" },
  fw_shipped:  { label: "Shipped",  color: "border-emerald-400/40 bg-emerald-400/[0.06] text-emerald-300" },
  fw_declined: { label: "Declined", color: "border-neutral-600 bg-neutral-800/50 text-neutral-500" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function daysAgo(iso) {
  if (!iso) return null;
  const ms = Date.now() - new Date(iso).getTime();
  return Math.floor(ms / 86400000);
}

export default function AdminFreeWorkflowPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("all");
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await client.get("/admin/free-workflow");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load free-workflow inbox");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const updateStatus = async (cid, status) => {
    try {
      await client.patch(`/admin/free-workflow/${cid}`, { status });
      toast.success(`Moved to ${STATUS_META[status]?.label || status}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Update failed");
    }
  };

  const saveNotes = async (cid, admin_notes) => {
    try {
      await client.patch(`/admin/free-workflow/${cid}`, { admin_notes });
      toast.success("Notes saved");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
  };

  const filtered = useMemo(() => {
    if (!data) return [];
    if (tab === "all") return data.applicants;
    if (tab === "overdue") {
      const now = Date.now();
      return data.applicants.filter((a) => {
        if (!["fw_scoping", "fw_building"].includes(a.status)) return false;
        return (now - new Date(a.created_at).getTime()) / 86400000 >= 7;
      });
    }
    return data.applicants.filter((a) => a.status === tab);
  }, [data, tab]);

  return (
    <div className="p-6 sm:p-8 space-y-6" data-testid="admin-free-workflow-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#FF9764] font-semibold mb-1 flex items-center gap-1.5">
            <Sparkle size={11} weight="fill" /> Free Workflow Ops
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">
            Free workflow queue
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Every visitor who clicked <span className="text-neutral-300">"Get One Workflow Automated Free"</span>{" "}
            lands here. Ship the promised workflow in ≤ 5 business days.
          </p>
        </div>
        <button
          onClick={load}
          data-testid="fw-refresh"
          className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition-colors"
        >
          Refresh
        </button>
        <button
          onClick={async () => {
            try {
              const r = await client.post("/admin/free-workflow/sla-run-now");
              toast.success(`SLA scan complete — scanned ${r.data?.scanned || 0}, alerted ${r.data?.fired || 0}`);
            } catch (e) {
              toast.error(e?.response?.data?.detail || "SLA scan failed");
            }
          }}
          data-testid="fw-sla-run-now"
          className="text-xs uppercase tracking-widest text-neutral-500 hover:text-white border border-white/10 hover:border-white/30 rounded-full px-3 py-1.5 transition-colors"
        >
          Run SLA scan
        </button>
      </div>

      <div className="grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-7" data-testid="fw-stats">
        <StatCell label="All" value={data?.stats?.all || 0} active={tab === "all"} onClick={() => setTab("all")} testid="fw-stat-all" />
        {Object.keys(STATUS_META).map((s) => (
          <StatCell
            key={s}
            label={STATUS_META[s].label}
            value={data?.stats?.[s] || 0}
            active={tab === s}
            onClick={() => setTab(s)}
            testid={`fw-stat-${s}`}
            accent={STATUS_META[s].color}
          />
        ))}
        <StatCell
          label="Overdue"
          value={data?.stats?.overdue || 0}
          active={tab === "overdue"}
          onClick={() => setTab("overdue")}
          testid="fw-stat-overdue"
          accent="border-red-400/40 bg-red-400/[0.06] text-red-300"
          urgent={data?.stats?.overdue > 0}
        />
      </div>

      {loading ? (
        <div className="text-sm text-neutral-500 py-16 text-center">Loading free-workflow leads…</div>
      ) : filtered.length === 0 ? (
        <div
          data-testid="fw-empty-state"
          className="rounded-xl border border-dashed border-white/10 py-16 text-center"
        >
          <div className="text-sm text-neutral-400 max-w-md mx-auto">
            {tab === "all"
              ? "No free-workflow requests yet. The landing page CTA fires them into this queue."
              : tab === "overdue"
              ? "Nothing overdue — good work."
              : `Nobody in ${STATUS_META[tab]?.label || tab} yet.`}
          </div>
        </div>
      ) : (
        <div className="space-y-2" data-testid="fw-list">
          {filtered.map((a) => (
            <LeadRow
              key={a.id}
              lead={a}
              open={openId === a.id}
              onToggle={() => setOpenId(openId === a.id ? null : a.id)}
              onStatus={(s) => updateStatus(a.id, s)}
              onSaveNotes={(notes) => saveNotes(a.id, notes)}
            />
          ))}
        </div>
      )}
    </div>
  );
}

const StatCell = ({ label, value, active, onClick, testid, accent, urgent }) => (
  <button
    onClick={onClick}
    data-testid={testid}
    className={`text-left rounded-xl border p-3.5 transition-colors ${
      active
        ? "border-white/30 bg-white/[0.04]"
        : urgent
        ? "border-red-400/40 bg-red-400/[0.04] hover:border-red-400/60"
        : "border-white/10 bg-white/[0.02] hover:border-white/20"
    }`}
  >
    <div className={`text-[10px] uppercase tracking-widest font-semibold mb-1.5 ${accent ? accent.split(" ")[2] : "text-neutral-500"} flex items-center gap-1`}>
      {urgent && <WarningOctagon size={10} weight="fill" />}
      {label}
    </div>
    <div className="text-2xl font-semibold tabular-nums">{value}</div>
  </button>
);

function LeadRow({ lead: a, open, onToggle, onStatus, onSaveNotes }) {
  const [notes, setNotes] = useState(a.admin_notes || "");
  const meta = STATUS_META[a.status] || STATUS_META.fw_new;
  const age = daysAgo(a.created_at);
  const isOverdue = ["fw_scoping", "fw_building"].includes(a.status) && age >= 7;

  return (
    <div
      className={`rounded-xl border overflow-hidden ${
        isOverdue ? "border-red-400/40 bg-red-400/[0.03]" : "border-white/10 bg-white/[0.02]"
      }`}
      data-testid={`fw-row-${a.id}`}
    >
      <button
        onClick={onToggle}
        data-testid={`fw-row-toggle-${a.id}`}
        className="w-full flex items-center gap-4 p-4 text-left hover:bg-white/[0.02] transition-colors"
      >
        <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] uppercase tracking-widest font-semibold border ${meta.color} shrink-0`}>
          {meta.label}
        </div>
        <div className="min-w-0 flex-1">
          <div className="text-sm text-white font-medium truncate">
            {a.contact_name}
            <span className="text-neutral-500 font-normal"> · {a.company_name}</span>
          </div>
          <div className="text-[11px] text-neutral-500 truncate">
            {a.email} · {a.phone || "no phone"} · fleet: {a.fleet_size || "—"}
          </div>
        </div>
        {isOverdue && (
          <span
            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-red-300 border border-red-400/40 bg-red-400/10 rounded-full px-2 py-0.5"
            title="In scoping/building > 7 days"
          >
            <Clock size={10} weight="bold" /> {age}d
          </span>
        )}
        <div className="text-[11px] text-neutral-500 tabular-nums shrink-0">
          {fmtDate(a.created_at)}
        </div>
      </button>
      {open && (
        <div className="border-t border-white/5 p-5 grid gap-4 md:grid-cols-2" data-testid={`fw-detail-${a.id}`}>
          <div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
              What they want automated
            </div>
            <div className="rounded-md bg-black/40 border border-white/5 p-3 text-sm text-neutral-200 leading-relaxed whitespace-pre-line min-h-[80px]">
              {a.current_challenges || <span className="text-neutral-600 italic">— nothing shared —</span>}
            </div>
            {a.services_of_interest && (
              <div className="mt-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/5 text-[11px] text-neutral-400">
                <ClipboardText size={11} /> {a.services_of_interest}
              </div>
            )}
          </div>
          <div className="space-y-4">
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Move to status
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(STATUS_META).map((s) => (
                  <button
                    key={s}
                    onClick={() => onStatus(s)}
                    disabled={a.status === s}
                    data-testid={`fw-status-${s}-${a.id}`}
                    className={`text-[11px] px-2.5 py-1.5 rounded-full border transition-colors ${
                      a.status === s
                        ? `${STATUS_META[s].color} cursor-default`
                        : "border-white/15 text-neutral-300 hover:border-white/40 hover:bg-white/5"
                    }`}
                  >
                    {STATUS_META[s].label}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-widest text-neutral-500 font-semibold mb-2">
                Private notes
              </div>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                onBlur={() => notes !== (a.admin_notes || "") && onSaveNotes(notes)}
                placeholder="Auto-saves on blur"
                rows={3}
                data-testid={`fw-notes-${a.id}`}
                className="w-full bg-black/40 border border-white/10 rounded-md px-3 py-2 text-sm focus:border-[#FF5A1F]/50 focus:outline-none placeholder:text-neutral-600 resize-none"
              />
            </div>
            <div className="flex flex-wrap gap-2">
              <a
                href={`mailto:${a.email}?subject=${encodeURIComponent(`Your free FreightPilot AI workflow — ${a.company_name}`)}`}
                data-testid={`fw-email-${a.id}`}
                className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors"
              >
                <EnvelopeSimple size={11} /> Email
              </a>
              {a.phone && (
                <a
                  href={`tel:${a.phone}`}
                  data-testid={`fw-call-${a.id}`}
                  className="text-[11px] inline-flex items-center gap-1 px-2.5 py-1.5 rounded-full border border-white/15 hover:border-white/40 hover:bg-white/5 transition-colors"
                >
                  <Phone size={11} /> Call
                </a>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
